import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import
    {
        MAT_DIALOG_DATA, MatDialogModule,
        MatDialogRef,
    } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatCardModule } from "@angular/material/card";
import { Observable, timer } from "rxjs";
import { Store } from "@ngxs/store";
import { ProductApiStatusState, PutNote, PutProduct, PutVersion } from "../../product/state";
import
    {
        Note,
        NoteType,
        Product,
        ProductType,
        UpdateNoteRequestModel,
        UpdateProductRequestModel,
        UpdateVersionRequestModel,
        Version,
        VersionType,
        Visibility
    } from "../../../services/api";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { AppState } from "../../../state";
import { MatOptionModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatTabsModule } from "@angular/material/tabs";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { EditTypeDialogData } from "../../../interfaces";
import { SubscriptionHandlerComponent } from "../subscription-handler/subscription-handler.component";

@Component({
    selector: 'app-edit-type-dialog',
    imports: [CommonModule, MatButtonModule, MatDialogModule, MatIconModule, MatInputModule, MatCardModule, FormsModule, MatOptionModule, MatSelectModule, MatDatepickerModule, MatFormFieldModule, ReactiveFormsModule, MatTabsModule, MatTooltipModule, MatProgressSpinnerModule],
    templateUrl: './edit-type-dialog.component.html',
    styleUrl: './edit-type-dialog.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
// Component to edit a type-value
export class EditTypeDialogComponent extends SubscriptionHandlerComponent implements OnInit
{
    editedValue$: Observable<boolean | null> = this.store.select(ProductApiStatusState.editedValue);

    productTypes$: Observable<ProductType[]> = this.store.select(AppState.productTypes);

    versionTypes$: Observable<VersionType[]> = this.store.select(AppState.versionTypes);

    visibilities$: Observable<Visibility[]> = this.store.select(AppState.visibilities);

    noteTypes$: Observable<NoteType[]> = this.store.select(AppState.noteTypes);

    product: Product;

    version: Version;

    note: Note;

    // Edit button pressed flag
    editPressed: boolean = false;

    // Edit disabled flag (equal values should not be editable)
    editDisabled: boolean = true;

    selectedProductType = new FormControl<ProductType | null>(null);

    selectedVersionType = new FormControl<VersionType | null>(null);

    selectedNoteType = new FormControl<NoteType | null>(null);

    selectedDisplayNoteType = new FormControl<NoteType | null>(null);

    selectedVisibility = new FormControl<Visibility | null>(null);

    blockReason = new FormControl<string>("", { nonNullable: true });

    constructor(@Inject(MAT_DIALOG_DATA) public data: EditTypeDialogData,
        private store: Store,
        private dialogRef: MatDialogRef<EditTypeDialogComponent>)
    {
        super();
    }

    ngOnInit()
    {
        this.blockReason.disable();
        // If-Statement for setting the Type-Value
        if (this.data.property === "Product")
        {
            if (this.data.field === "Product Type")
            {
                this.subscribeTo(this.productTypes$, productTypes =>
                {
                    if (productTypes)
                    {
                        this.selectedProductType.setValue(productTypes.find(x =>
                            x.name === this.data.value
                        ) ?? productTypes[0]);
                    }
                });
            } else if (this.data.field === "Visibility")
            {
                this.subscribeTo(this.visibilities$, visibilities =>
                {
                    if (visibilities)
                    {
                        this.selectedVisibility.setValue(visibilities.find(x =>
                            x.name === this.data.value
                        ) ?? visibilities[0]);
                    }
                });
            }
        } else if (this.data.property === "Version")
        {
            if (this.data.field === "Version Type")
            {
                this.subscribeTo(this.versionTypes$, versionTypes =>
                {
                    if (versionTypes && versionTypes.length > 0)
                    {
                        this.selectedVersionType.setValue(versionTypes.find(x =>
                        {
                            x.name === this.data.value;
                        }
                        ) ?? versionTypes[0]);
                        if (this.data.text != "")
                        {
                            this.blockReason.setValue(this.data.text);
                            this.blockReason.enable();
                        }
                    }
                });
            } else if (this.data.field === "Visibility")
            {
                this.subscribeTo(this.visibilities$, visibilities =>
                {
                    if (visibilities)
                    {
                        this.selectedVisibility.setValue(visibilities.find(x =>
                            x.name === this.data.value
                        ) ?? visibilities[0]);
                    }
                });
            }
        } else if (this.data.property === "Note")
        {
            if (this.data.field === "Note Type")
            {
                this.subscribeTo(this.noteTypes$, noteTypes =>
                {
                    if (noteTypes)
                    {
                        this.selectedNoteType.setValue(noteTypes.find(x =>
                            x.name === this.data.value
                        ) ?? noteTypes[0]);
                    }
                });
            } else if (this.data.field === "Visibility")
            {
                this.subscribeTo(this.visibilities$, visibilities =>
                {
                    if (visibilities)
                    {
                        this.selectedVisibility.setValue(visibilities.find(x =>
                            x.name === this.data.value
                        ) ?? visibilities[0]);
                    }
                });
            } else if (this.data.field === "Display Note Type")
            {
                this.subscribeTo(this.noteTypes$, noteTypes =>
                {
                    if (noteTypes)
                    {
                        this.selectedDisplayNoteType.setValue(noteTypes.find(x =>
                            x.name === this.data.value
                        ) ?? noteTypes[0]);
                    }
                });
            }
        }
    }

    // Enter functionality for editing Type-Value
    enterEdit()
    {
        if (!this.editDisabled && !this.editPressed)
        {
            this.editValue();
        }
    }

    // Change of Product Type
    changeProductType()
    {
        // Disable Create-Button if the original value is identical to the current value
        this.editDisabled = this.selectedProductType.value?.name === this.data.value;
    }

    // Change of Version Type
    changeVersionType()
    {
        if (!this.selectedVersionType.value) return;

        // Disable Block-Reason if the Version Type is not "Blocked"
        if (this.selectedVersionType.value.id !== 4)
            this.blockReason.disable();
        else
            this.blockReason.enable();

        // Disable Create-Button if the original value is identical to the current value
        this.editDisabled = this.selectedVersionType.value.name === this.data.value;
    }

    // Change of Visibility
    changeVisibility()
    {
        if (!this.selectedVisibility.value) return;

        // Disable Create-Button if the original value is identical to the current value
        this.editDisabled = this.selectedVisibility.value.name === this.data.value;
    }

    // Change of Note Type
    changeNoteType()
    {
        if (!this.selectedNoteType.value) return;
        // if (this.note) {
        //     if (this.note.noteType?.id == this.note.displayNoteType?.id) {
        //         this.dialog.open(AlertDialogComponent, {
        //             data: "Changes to the Note Type will be applied to the Display Note Type as well.",
        //             disableClose: true,
        //             width: 'auto',
        //             height: 'auto'
        //         });
        //     }
        // }

        // Disable Create-Button if the original value is identical to the current value
        this.editDisabled = this.selectedNoteType.value.name === this.data.value;
    }

    // Change of Display Note Type
    changeDisplayNoteType()
    {
        if (!this.selectedDisplayNoteType.value) return;

        this.editDisabled = this.selectedDisplayNoteType.value.name === this.data.value;
    }

    editValue()
    {
        this.editPressed = true;
        // If-Statement for editing every possible value
        if (this.data.property === "Product")
        {
            if (this.data.field === "Product Type")
            {
                if (!this.selectedProductType.value) return;
                let updatedProduct: UpdateProductRequestModel = {
                    id: this.product.id,
                    productTypeId: this.selectedProductType.value.id,
                }
                this.store.dispatch(new PutProduct(updatedProduct))
            } else if (this.data.field === "Visibility")
            {
                if (!this.selectedVisibility.value) return;
                let updatedProduct: UpdateProductRequestModel = {
                    id: this.product.id,
                    visibilityId: this.selectedVisibility.value.id,
                }
                this.store.dispatch(new PutProduct(updatedProduct))
            }
        } else if (this.data.property == "Version")
        {
            if (this.data.field === "Version Type")
            {
                let updatedVersion: UpdateVersionRequestModel;
                if (!this.selectedVersionType.value) return;
                if (this.selectedVersionType.value.id === 4)
                {
                    updatedVersion = {
                        id: this.version.id,
                        versionTypeId: this.selectedVersionType.value.id,
                        banReason: this.blockReason.value,
                    }
                } else
                {
                    updatedVersion = {
                        id: this.version.id,
                        versionTypeId: this.selectedVersionType.value.id,
                    }
                }
                this.store.dispatch(new PutVersion(updatedVersion))
            } else if (this.data.field === "Visibility")
            {
                if (!this.selectedVisibility.value) return;
                let updatedVersion: UpdateVersionRequestModel = {
                    id: this.version.id,
                    visibilityId: this.selectedVisibility.value.id,
                }
                this.store.dispatch(new PutVersion(updatedVersion))
            }
        } else if (this.data.property == "Note")
        {
            if (this.data.field === "Note Type")
            {
                if (!this.selectedNoteType.value) return;
                let updatedNote: UpdateNoteRequestModel = {
                    id: this.note.id,
                    noteTypeId: this.selectedNoteType.value.id,
                }
                this.store.dispatch(new PutNote(updatedNote))
            } else if (this.data.field === "Visibility")
            {
                if (!this.selectedVisibility.value) return;
                let updatedNote: UpdateNoteRequestModel = {
                    id: this.note.id,
                    visibilityId: this.selectedVisibility.value.id,
                }
                this.store.dispatch(new PutNote(updatedNote))
            } else if (this.data.field === "Display Note Type")
            {
                if (!this.selectedDisplayNoteType.value) return;
                let updatedNote: UpdateNoteRequestModel = {
                    id: this.note.id,
                    displayNoteTypeId: this.selectedDisplayNoteType.value.id,
                }
                this.store.dispatch(new PutNote(updatedNote))
            }
        }

        // Close the dialog 2 seconds after editing
        this.subscribeTo(this.editedValue$, x =>
        {
            if (x) timer(2000).subscribe(() => this.dialogRef.close(true))
        });
    }
}

